import React, { useEffect, useRef, useState } from "react"
import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"
import { enqueueSnackbar } from "notistack"
import Skeleton from "react-loading-skeleton"

import Filter from "./Filter"
import Table from "./Table"
import Cart from "./Cart"

let searchTimeout

function Catalogue() {
  const deviceDropdownRef = useRef(null)

  const [parts, setParts] = useState({
    loading: false,
    data: [],
    search: "",
    deviceId: "",
    update: false,
  })

  const [addingNumber, setAddingNumber] = useState(null)

  const [cart, setCart] = useState({
    loading: true,
    basket: null,
    removingIndex: null,
    ordering: false,
    transportCode: "",
  })

  // a search is only performed once the user types a keyword or picks a device
  const hasQuery =
    Boolean((parts.search || "").trim()) || Boolean(parts.deviceId)

  // load parts whenever the search term or selected device changes
  useEffect(() => {
    if (parts.update) {
      loadParts()
    }
  }, [parts.update])

  useEffect(() => {
    loadBasket()
  }, [])

  const loadParts = () => {
    // nothing to search for yet - keep the "start searching" hint visible
    if (!hasQuery) {
      setParts((prev) => ({ ...prev, loading: false, data: [] }))
      return
    }

    setParts((prev) => ({ ...prev, loading: true }))

    // when a device is selected we search within that device's parts,
    // otherwise we use the generic keyword search
    let url = parts.deviceId ? "aswo/find-device-parts" : "aswo/find-parts"

    let payload = { search: parts.search }

    if (parts.deviceId) {
      payload.device_id = parts.deviceId
    }

    Api.post(url, payload)
      .then((res) => {
        setParts((prev) => ({
          ...prev,
          loading: false,
          data: Array.isArray(res.data.items) ? res.data.items : [],
        }))
      })
      .catch(() => {
        setParts((prev) => ({ ...prev, loading: false, data: [] }))
        enqueueSnackbar(trans("catalogue.loadError"), { variant: "error" })
      })
  }

  const loadBasket = () => {
    setCart((prev) => ({ ...prev, loading: true }))

    Api.get("aswo/basket")
      .then((res) => {
        setCart((prev) => ({ ...prev, loading: false, basket: res.data }))
      })
      .catch(() => {
        setCart((prev) => ({ ...prev, loading: false }))
      })
  }

  const handleSearch = (value) => {
    clearTimeout(searchTimeout)

    setParts((prev) => ({ ...prev, search: value }))

    searchTimeout = setTimeout(() => {
      setParts((prev) => ({ ...prev, update: new Date().getTime() }))
    }, 400)
  }

  const handleDevice = (deviceId) => {
    setParts((prev) => ({
      ...prev,
      deviceId: deviceId,
      update: new Date().getTime(),
    }))
  }

  const handleAddToCart = (number, quantity) => {
    setAddingNumber(number)

    Api.post("aswo/add-to-basket", { number, quantity })
      .then(() => {
        enqueueSnackbar(trans("catalogue.addedToCart"), { variant: "success" })
        loadBasket()
      })
      .catch(() => {
        enqueueSnackbar(trans("messages.fail"), { variant: "error" })
      })
      .finally(() => {
        setAddingNumber(null)
      })
  }

  const handleRemove = (index) => {
    setCart((prev) => ({ ...prev, removingIndex: index }))

    Api.post("aswo/remove-from-basket", { index })
      .then(() => {
        enqueueSnackbar(trans("catalogue.removedFromCart"), {
          variant: "success",
        })
        loadBasket()
      })
      .catch(() => {
        enqueueSnackbar(trans("messages.fail"), { variant: "error" })
      })
      .finally(() => {
        setCart((prev) => ({ ...prev, removingIndex: null }))
      })
  }

  const handleTransport = (code) => {
    setCart((prev) => ({ ...prev, transportCode: code }))
  }

  const handleOrder = () => {
    // a shipment method must be picked before the order can be completed
    if (!cart.transportCode) {
      enqueueSnackbar(trans("catalogue.selectShippingFirst"), {
        variant: "warning",
      })
      return
    }

    setCart((prev) => ({ ...prev, ordering: true }))

    Api.post("aswo/order", { ship_mode: cart.transportCode })
      .then((res) => {
        if (res.data.success) {
          enqueueSnackbar(trans("catalogue.orderPlaced"), {
            variant: "success",
          })
          loadBasket()
        } else {
          enqueueSnackbar(trans("catalogue.orderFailed"), { variant: "error" })
        }
      })
      .catch(() => {
        enqueueSnackbar(trans("catalogue.orderFailed"), { variant: "error" })
      })
      .finally(() => {
        setCart((prev) => ({ ...prev, ordering: false }))
      })
  }

  return (
    <div className="catalogue-layout">
      <div className="catalogue-main">
        <div className="pannel">
          <Filter
            search={parts.search}
            handleSearch={handleSearch}
            handleDevice={handleDevice}
            deviceDropdownRef={deviceDropdownRef}
          />
          <hr />
          {parts.loading ? (
            <Skeleton height={45} count={10} />
          ) : !hasQuery ? (
            <div className="not-found">
              <p>{trans("catalogue.startSearchHint")}</p>
            </div>
          ) : (
            <Table
              parts={parts.data}
              addingNumber={addingNumber}
              handleAddToCart={handleAddToCart}
            />
          )}
        </div>
      </div>

      <div className="catalogue-cart">
        <Cart
          loading={cart.loading}
          basket={cart.basket}
          removingIndex={cart.removingIndex}
          ordering={cart.ordering}
          transportCode={cart.transportCode}
          handleTransport={handleTransport}
          handleRemove={handleRemove}
          handleOrder={handleOrder}
        />
      </div>
    </div>
  )
}

export default Catalogue
